import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { assignmentService } from '../services/apiService'

const Assignments = () => {
  const [assignments, setAssignments] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, completed, pending

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [assignmentsRes, submissionsRes] = await Promise.all([
        assignmentService.getAssignments(),
        assignmentService.getMySubmissions(),
      ])

      const assignmentsData = assignmentsRes.data.results || assignmentsRes.data
      const submissionsData = submissionsRes.data.results || submissionsRes.data

      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : [])
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : [])
    } catch (error) {
      console.error('Error fetching assignments:', error)
    } finally {
      setLoading(false)
    }
  }

  const isSubmitted = (assignmentId) => {
    return submissions.some((sub) => sub.assignment === assignmentId)
  }

  const getSubmission = (assignmentId) => {
    return submissions.find((sub) => sub.assignment === assignmentId)
  }

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date()
  }

  const filteredAssignments = assignments.filter((assignment) => {
    if (filter === 'completed') return isSubmitted(assignment.id)
    if (filter === 'pending') return !isSubmitted(assignment.id)
    return true
  })

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900'>My Assignments</h1>
        <p className='text-gray-600 mt-2'>
          View and submit assignments from your enrolled courses
        </p>
      </div>

      {/* Filter Tabs */}
      <div className='border-b border-gray-200 mb-6'>
        <nav className='-mb-px flex space-x-8'>
          <button
            onClick={() => setFilter('all')}
            className={`${
              filter === 'all'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All ({assignments.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`${
              filter === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Pending ({assignments.filter((a) => !isSubmitted(a.id)).length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`${
              filter === 'completed'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Completed ({submissions.length})
          </button>
        </nav>
      </div>

      {filteredAssignments.length > 0 ? (
        <div className='space-y-4'>
          {filteredAssignments.map((assignment) => {
            const submission = getSubmission(assignment.id)
            const overdue = isOverdue(assignment.due_date) && !submission

            return (
              <Link
                key={assignment.id}
                to={`/assignments/${assignment.id}`}
                className='block bg-white rounded-lg shadow hover:shadow-md transition-shadow'
              >
                <div className='p-6'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                        {assignment.title}
                      </h3>
                      <p className='text-sm text-gray-600 mb-3 line-clamp-2'>
                        {assignment.description}
                      </p>
                      <div className='flex items-center space-x-4 text-sm'>
                        <span
                          className={`flex items-center ${
                            overdue ? 'text-red-600' : 'text-gray-500'
                          }`}
                        >
                          <svg
                            className='h-4 w-4 mr-1'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                            />
                          </svg>
                          Due: {new Date(assignment.due_date).toLocaleString()}
                          {overdue && ' (Overdue)'}
                        </span>
                        <span className='text-gray-500'>
                          Max Score: {assignment.max_score} points
                        </span>
                      </div>
                    </div>
                    <div className='ml-4 flex-shrink-0'>
                      {submission ? (
                        <div className='text-right'>
                          {submission.is_graded ? (
                            <div>
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                  submission.score >= assignment.max_score * 0.7
                                    ? 'bg-green-100 text-green-800'
                                    : submission.score >=
                                        assignment.max_score * 0.5
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {submission.score}/{assignment.max_score}
                              </span>
                            </div>
                          ) : (
                            <span className='inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
                              Submitted
                            </span>
                          )}
                        </div>
                      ) : (
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            overdue
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          Not Submitted
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className='text-center py-12 bg-white rounded-lg shadow'>
          <svg
            className='mx-auto h-12 w-12 text-gray-400 mb-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
          <p className='text-gray-500 mb-2'>
            {filter === 'all'
              ? 'No assignments yet'
              : filter === 'pending'
                ? 'No pending assignments'
                : 'No completed assignments'}
          </p>
          <p className='text-sm text-gray-400'>
            Assignments from your enrolled courses will appear here
          </p>
        </div>
      )}
    </div>
  )
}

export default Assignments
